'use client'

import { useEffect, useRef, useState } from 'react'
import { fetchShowcaseForPage, fetchShowcaseStats } from '../lib/showcaseApi'
import { FACTS } from '../lib/facts'
import { ShowcaseStack } from './ShowcasePair'
import { motion } from 'framer-motion'
// Stat values arrive as one string, e.g. "90+" or "550+", and are rendered as
// is. They used to count up from 0, which left the server HTML (and anything
// without JS) showing "0+".
function ShowcaseStat({ stat, index }) {
  return (
    <motion.div
      className="se-stat"
      style={{ transformPerspective: 900 }}
      initial={{ opacity: 0, z: -240, y: 16 }}
      whileInView={{ opacity: 1, z: 0, y: 0 }}
      whileHover={{ z: 30, transition: { type: 'spring', stiffness: 260, damping: 22 } }}
      viewport={{ once: true, amount: 0.5 }}
      transition={{ type: 'spring', stiffness: 90, damping: 16, mass: 0.9, delay: index * 0.08 }}
    >
      <strong className="se-stat-value">
        {String(stat.value).replace(/\+$/, '')}
        {/\+$/.test(String(stat.value)) ? <span className="se-stat-suffix">+</span> : null}
      </strong>
      <span className="se-stat-label">{stat.label}</span>
    </motion.div>
  )
}

// First-render data comes from the build-time snapshot (see
// scripts/generate-showcase-snapshot.mjs), read on the server and passed in
// as initialItems/initialStats (only this page's cards, not the whole file),
// so crawlers and no-JS visitors get real content in the server HTML. If the
// snapshot has no stat tiles at all, fall back to the figures in
// lib/facts.ts (same rule as the homepage band: a null fact is never
// rendered).
function startingStats(rows) {
  if (Array.isArray(rows) && rows.length) return rows
  return FACTS.homepageStats
    .filter((s) => s.value !== null)
    .map((s) => ({ id: s.label, value: `${s.value}${s.suffix}`, label: s.label.toUpperCase() }))
}

// `head` ({ eyebrow, title, highlight, text }) replaces the default
// agency-facing heading, e.g. on the business-owner page. `children` replaces
// the showcase cards (the client cards on the industry pages).
export default function ShippedEvidenceSection({ pageKey, head, initialItems = [], initialStats = [], children }) {
  const [items, setItems] = useState(initialItems)
  const [stats, setStats] = useState(() => startingStats(initialStats))
  const statBarRef = useRef(null)

  // Refresh from the live API after hydration. A failed or empty response
  // never replaces the snapshot, so the section can't blank out or show an
  // error.
  useEffect(() => {
    let active = true
    if (!pageKey) return () => {}
    Promise.all([fetchShowcaseForPage(pageKey), fetchShowcaseStats()]).then(([itemsData, statsData]) => {
      if (!active) return
      if (Array.isArray(itemsData) && itemsData.length) setItems(itemsData)
      if (Array.isArray(statsData) && statsData.length) setStats(statsData)
    })
    return () => {
      active = false
    }
  }, [pageKey])



  return (
    <section className="section shipped-evidence-section">
      <div className="container shipped-evidence-inner">
        {/* <header className="shipped-evidence-head">
          <span className="eyebrow-label">{eyebrow}</span>
          <h2 >Built in public. Adopted <br/> <span>Real Results.</span> </h2>
          {subheading ? <p className="se-subhead">{subheading}</p> : null}
        </header> */}

      <motion.div
          className="section-title centered why-choose-reference-head"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.5 }}
        >
          {head ? (
            <>
              <span className="eyebrow-label">{head.eyebrow}</span>
              <h2>{head.title} {head.highlight ? <span>{head.highlight}</span> : null}</h2>
              {head.text ? <p>{head.text}</p> : null}
            </>
          ) : (
            <>
              <span className="eyebrow-label">Why do agencies hire GHL Prime instead of a freelancer?</span>

              <h2>Focused Expertise. Real <br/> <span>Delivery Power.</span></h2>
              <p>GHL Prime is a specialized team backed by Octopi Digital. We don’t do everything. We go deep on GHL, automation, AI, and custom development, so your agency has the best possible team behind it.</p>
            </>
          )}
        </motion.div>
        {/* Ref lives on this wrapper rather than on the conditional content
            below: useInView reads the DOM node at mount, and this element is
            the first one in the tree that is always present, even before
            the stats have loaded. Attaching it to the stats.length-gated div
            instead means useInView initializes against a node that does not
            exist yet on the first render and never notices the real one
            mounting later, so the count-up simply never starts. */}
        <div className="se-stat-bar" ref={statBarRef}>
          {stats.map((stat, i) => (
            <ShowcaseStat key={stat.id} stat={stat} index={i} />
          ))}
        </div>

        {children ?? (items.length > 0 ? <ShowcaseStack items={items} /> : null)}
      </div>
    </section>
  )
}
